import { SearchOutlined } from "@ant-design/icons"
import { Flex, Select, Skeleton, Switch, Typography } from "antd"
import { type BaseOptionType } from "antd/lib/select"
import React, { useCallback, useEffect, useMemo, useState } from "react"

import { SearchBar } from "../../../../../../../../components/Input/SearchBar.tsx"
import { useAppSelector } from "../../../../../../../../hooks/use-app-selector.ts"
import type {
  ProjectChecklistFilterOptions,
  ProjectChecklistFilters
} from "../../../../../../redux/project.types.ts"
import { ProjectViewChecklistsAssignedSelect } from "./ProjectViewChecklistsAssignedSelect.tsx"

type SelectFilter = {
  key: keyof ProjectChecklistFilters
  placeholder: string
  className?: string
  showSearch?: boolean
  optionKey: keyof ProjectChecklistFilterOptions
}

const selectFilters: SelectFilter[] = [
  {
    key: "priority",
    placeholder: "Priority",
    className: "w-28",
    optionKey: "priority"
  },
  {
    key: "status",
    placeholder: "Status",
    className: "w-32",
    optionKey: "status"
  },
  {
    key: "project_type",
    placeholder: "Project Type",
    className: "w-40",
    optionKey: "project_type",
    showSearch: true
  },
  {
    key: "project_phase",
    placeholder: "Milestone",
    className: "w-52",
    optionKey: "project_phases",
    showSearch: true
  }
]

const filterOption = (input: string, option: any) =>
  (option?.label as string)?.toLowerCase().includes(input.toLowerCase())

interface ProjectViewChecklistsListFiltersProps {
  filters: ProjectChecklistFilters
  showFilterByAssignee: boolean
  onFilterChange: (data: Partial<ProjectChecklistFilters>) => void
  getFilterOptions: () => Promise<ProjectChecklistFilterOptions>
}

export function ProjectViewChecklistsListFilters({
  filters,
  showFilterByAssignee,
  onFilterChange,
  getFilterOptions
}: ProjectViewChecklistsListFiltersProps) {
  const options = useAppSelector((s) => s?.project?.checklistItemsFilterOptions)

  const [fetching, setFetching] = useState(false)

  const projectTypeOptions = options?.["project_type"]
  const projectTypeMap: Map<string, BaseOptionType[]> = useMemo(() => {
    return new Map(
      projectTypeOptions?.map((item) => [item?.value, item?.milestones])
    )
  }, [projectTypeOptions])

  const mutatedOptions = useMemo(() => {
    if (filters?.project_type) {
      return {
        ...options,
        project_phases: projectTypeMap.get(filters.project_type)
      }
    }
    return options
  }, [filters.project_type, options, projectTypeMap])

  const handleGetFilterOptions = useCallback(async () => {
    try {
      setFetching(true)
      await getFilterOptions()
    } finally {
      setFetching(false)
    }
  }, [getFilterOptions])

  const handleSelectChange = (key: keyof ProjectChecklistFilters) => {
    return (value: string | boolean | undefined | null) =>
      onFilterChange({
        [key]: value || null,
        ...(key === "project_type" && filters?.project_phase
          ? { project_phase: null }
          : {})
      })
  }

  useEffect(() => {
    void handleGetFilterOptions()
  }, [handleGetFilterOptions])

  if (fetching) {
    return <Loader showFilterByAssignee={showFilterByAssignee} />
  }

  return (
    <Flex justify="space-between" align="center" wrap gap={12}>
      <Flex align="center" gap={16} wrap>
        {selectFilters.map(
          ({ key, placeholder, className, optionKey, showSearch }) => (
            <Select
              key={key}
              value={filters?.[key] as string}
              allowClear
              placeholder={placeholder}
              className={className}
              options={mutatedOptions?.[optionKey]}
              showSearch={showSearch}
              filterOption={showSearch ? filterOption : undefined}
              onChange={handleSelectChange(key)}
            />
          )
        )}
        {showFilterByAssignee && (
          <ProjectViewChecklistsAssignedSelect
            value={filters?.assigned_to}
            onChange={handleSelectChange("assigned_to")}
            className={"w-52"}
            options={options}
          />
        )}
      </Flex>

      <Flex align="center" gap={12}>
        <Flex align="center" gap={8}>
          <Switch
            value={filters?.overdue}
            onChange={handleSelectChange("overdue")}
          />
          <Typography.Text type="secondary">Show Overdue Only</Typography.Text>
        </Flex>
        <SearchBar
          placeholder="Search by name.."
          prefix={<SearchOutlined className="text-gray-400" />}
          className="w-48 md:w-64 flex-none"
          defaultValue={filters?.q || ""}
          onSearch={handleSelectChange("q")}
        />
      </Flex>
    </Flex>
  )
}

const Loader = ({
  showFilterByAssignee
}: {
  showFilterByAssignee: boolean
}) => {
  return (
    <Flex justify="space-between" align="center" wrap gap={12}>
      <Flex align="center" gap={16} wrap>
        {selectFilters?.map((option) => (
          <Skeleton.Button
            key={`loader-${option?.key}`}
            className={option?.className}
          />
        ))}
        {showFilterByAssignee && <Skeleton.Button className={"w-52"} />}
      </Flex>

      <Flex align="center" gap={12}>
        <Skeleton.Button className={"w-44"} />
        <Skeleton.Button className={"w-48 md:w-64"} />
      </Flex>
    </Flex>
  )
}
